import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, FileSignature, Upload } from 'lucide-react';
import { losApi } from './losApi';
import { downloadWithAuth } from './api';
import { toast } from '../hooks/use-toast';
import { errorMessage } from './errors';
import { formatDate } from './kit/time';
import { useAuth } from './AuthContext';
import { Input } from '../components/ui/input';

const SIGNERS = [['borrower', 'Borrower'], ['coborrower', 'Co-Borrower'], ['lo', 'Loan officer']];
const SOURCE = { docusign: 'DocuSign', wet_signature: 'Wet signature' };

// ACTION_KEYS["urla.record_signature"] -> staff.manage. The server enforces it; this only hides the button.
const canRecordSignature = (user) => !!(user?.is_super_admin || (user?.permissions || []).includes('staff.manage'));

const show = (v) => (v === null || v === undefined || v === '' ? '(empty)' : String(v));

/**
 * Signed state of the 1003 (ticket 192, D20): who signed on which snapshot, whether the live
 * form changed since, and the owner / admin "Record wet signature" upload. Nothing here
 * signs: a signature comes from a completed DocuSign envelope or the paper scan.
 */
export default function UrlaSigningPanel({ loan, refreshKey, onRecorded, unsaved }) {
  const { user } = useAuth() || {};
  const loanId = loan.loan_file_id;
  const [state, setState] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    try { setState(await losApi.urlaSigning(loanId)); }
    catch { setState({ snapshots: [], status: { signed: false, changed: false, diff: [] }, error: true }); }
  }, [loanId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  if (!state) return null;
  const { snapshots, status } = state;
  const hasCo = !!loan.urla?.coborrower;
  const canRecord = canRecordSignature(user);

  return (
    <div className="space-y-2" data-testid="urla-signing">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {SIGNERS.filter(([k]) => k !== 'coborrower' || hasCo).map(([k, l]) => {
          const at = loan.urla?.[`${k}_signed_at`];
          return at ? (
            <span key={k} className="px-2.5 py-1.5 rounded-lg font-semibold" style={{ background: '#F3FAE8', color: '#4c7f21' }} data-testid={`urla-signed-${k}`}>
              {l} signed {formatDate(at)}
            </span>
          ) : (
            <span key={k} className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-500" data-testid={`urla-unsigned-${k}`}>{l} not signed</span>
          );
        })}
        {status.signed && (
          <span className="text-slate-500" data-testid="urla-signed-version">
            Signed version {status.signed_version} · {SOURCE[status.source] || status.source}
          </span>
        )}
        {canRecord && (
          <button type="button" onClick={() => setShowForm((v) => !v)} className="ml-auto text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1.5" data-testid="urla-wet-open">
            <FileSignature size={12} /> Record wet signature
          </button>
        )}
      </div>

      {status.changed && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="urla-changed-since-signing">
          <div className="font-semibold">
            Changed since signing: live version {status.live_version}, signed version {status.signed_version}
          </div>
          <ul className="mt-1.5 space-y-0.5 max-h-48 overflow-auto font-mono text-[11px]" data-testid="urla-diff">
            {status.diff.map((d) => (
              <li key={d.path}>
                {d.path}: <span className="line-through opacity-70">{show(d.before)}</span> → {show(d.after)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {showForm && canRecord && (
        <WetSignatureForm loan={loan} hasCo={hasCo} unsaved={unsaved} onDone={() => { setShowForm(false); load(); onRecorded && onRecorded(); }} />
      )}

      {snapshots.length > 0 && (
        <details className="text-xs text-slate-600" data-testid="urla-snapshots">
          <summary className="cursor-pointer font-semibold">Signed copies ({snapshots.length})</summary>
          <ul className="mt-1.5 space-y-1">
            {snapshots.map((s) => (
              <li key={s.snapshot_id} className="flex flex-wrap items-center gap-2" data-testid={`urla-snapshot-${s.version}`}>
                <span>v{s.version} · {SOURCE[s.source] || s.source} · {formatDate(s.signed_at)}</span>
                <span className="text-slate-400">{(s.signers || []).join(', ')}</span>
                {s.document_id && (
                  <button type="button" className="underline" onClick={() => downloadWithAuth(losApi.documentPath(s.document_id), `signed-1003-v${s.version}.pdf`)}>
                    Download signed copy
                  </button>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function WetSignatureForm({ loan, hasCo, unsaved, onDone }) {
  const [signers, setSigners] = useState(['borrower']);
  const [signedOn, setSignedOn] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  const toggle = (k) => setSigners((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const submit = async () => {
    setBusy(true);
    try {
      await losApi.recordWetSignature(loan.loan_file_id, { file, signers, signedOn });
      toast({ title: 'Wet signature recorded', description: 'The 1003 as it stands now is saved as the signed copy.' });
      onDone();
    } catch (e) {
      toast({ title: 'Failed', description: errorMessage(e) });
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-3 text-xs" data-testid="urla-wet-form">
      <div className="text-slate-600">
        Upload the signed paper 1003. The saved 1003 is frozen as the signed copy; later edits show as changed since signing.
      </div>
      {unsaved && <div className="text-amber-700" data-testid="urla-wet-unsaved">Save the 1003 first: the signed copy is the saved version.</div>}
      <div className="flex flex-wrap gap-3">
        {SIGNERS.filter(([k]) => k !== 'coborrower' || hasCo).map(([k, l]) => (
          <label key={k} className="inline-flex items-center gap-1.5">
            <input type="checkbox" checked={signers.includes(k)} onChange={() => toggle(k)} data-testid={`urla-wet-signer-${k}`} /> {l}
          </label>
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-slate-500">Date signed</span>
          <Input type="date" max={today} value={signedOn} onChange={(e) => setSignedOn(e.target.value)} className="mt-1 w-auto px-2" data-testid="urla-wet-date" />
        </label>
        <label className="block">
          <span className="text-slate-500">Signed 1003 (PDF or image)</span>
          <input type="file" accept="application/pdf,image/png,image/jpeg" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1 block text-sm" data-testid="urla-wet-file" />
        </label>
        <button type="button" onClick={submit} disabled={busy || unsaved || !file || !signedOn || signers.length === 0} className="text-xs font-semibold px-3 py-2 rounded-lg text-white inline-flex items-center gap-1.5 disabled:opacity-40" style={{ background: '#070B5F' }} data-testid="urla-wet-submit">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Record signature
        </button>
      </div>
    </div>
  );
}
